/**
 * Coordinates, as the route map needs them.
 *
 * Kept apart from Leaflet so the parts that can be reasoned about — is this
 * point real, how far apart are these two, what does that read as — are plain
 * functions a test can call without a DOM.
 */

/** A WGS84 point in Leaflet's order: latitude first. */
export interface LatLng {
  lat: number
  lng: number
}

/** One end of a journey: where it is, and what the booking calls it. */
export interface RouteStop {
  address: string | null
  point: LatLng | null
}

export interface RouteTarget {
  /** What the dialog titles itself with — a booking reference, or `#id`. */
  reference: string
  pickup: RouteStop
  dropoff: RouteStop
}

/**
 * The coordinate-bearing shape both screens send here.
 *
 * `AdminTrip` and `AdminTripRequest` are different records with different
 * lifecycles, but the six fields a map cares about are identical on both, so
 * neither page needs its own adapter.
 */
export interface MappableBooking {
  id: number
  bookingReference: string | null
  pickupAddress: string | null
  pickupLatitude: number | null
  pickupLongitude: number | null
  dropoffAddress: string | null
  dropoffLatitude: number | null
  dropoffLongitude: number | null
}

/**
 * A pair of numbers, or null if they cannot be put on a map.
 *
 * Rejects (0, 0) deliberately. Both backends store coordinates in columns that
 * were once primitive, so a booking that never had a drop-off — every
 * open-ended TRAVEL trip — can arrive as an exact zero pair rather than as
 * absent. Trusting it would draw a route from Cairo to the Atlantic and zoom
 * the map out to fit both.
 */
export function coordinate(
  lat: number | null | undefined,
  lng: number | null | undefined,
): LatLng | null {
  if (typeof lat !== 'number' || typeof lng !== 'number') return null
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
  if (lat === 0 && lng === 0) return null
  return { lat, lng }
}

export function toRouteTarget(booking: MappableBooking): RouteTarget {
  return {
    reference: booking.bookingReference ?? `#${booking.id}`,
    pickup: {
      address: booking.pickupAddress,
      point: coordinate(booking.pickupLatitude, booking.pickupLongitude),
    },
    dropoff: {
      address: booking.dropoffAddress,
      point: coordinate(booking.dropoffLatitude, booking.dropoffLongitude),
    },
  }
}

/** Whether there is anything at all worth opening a map for. */
export function hasSomethingToShow(target: RouteTarget): boolean {
  return target.pickup.point !== null || target.dropoff.point !== null
}

const EARTH_RADIUS_METRES = 6_371_008.8

/** Great-circle distance — the straight line, when there is no road answer. */
export function haversineMetres(from: LatLng, to: LatLng): number {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180

  const lat1 = toRadians(from.lat)
  const lat2 = toRadians(to.lat)
  const deltaLat = toRadians(to.lat - from.lat)
  const deltaLng = toRadians(to.lng - from.lng)

  const a =
    Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2

  return 2 * EARTH_RADIUS_METRES * Math.asin(Math.min(1, Math.sqrt(a)))
}

/** "840 m" under a kilometre, "12.4 km" over it. */
export function formatDistance(metres: number): string {
  if (!Number.isFinite(metres) || metres < 0) return '—'
  if (metres < 1000) return `${Math.round(metres)} m`

  const km = metres / 1000
  return `${km < 100 ? km.toFixed(1) : Math.round(km)} km`
}

/** Driving time as an operator reads it off a dispatch board: "1 h 12 min". */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—'

  const totalMinutes = Math.round(seconds / 60)
  if (totalMinutes < 1) return 'under a minute'
  if (totalMinutes < 60) return `${totalMinutes} min`

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`
}
